// Browser-side equivalent of the old export-site-bundle edge function.
//
// Layout:
//   {property_label}/
//     INFO.txt
//     MANIFEST.txt              files > MAX_EMBED_BYTES listed here with URL
//     digital_twin/{category}/{filename}
//     photos/...                same layout as property-photos bundle
//     data/survey.xlsx
//     data/assets.xlsx

import JSZip from "jszip";
import * as XLSX from "xlsx-js-style";
import type { BuildContext } from "@/features/exports/services/DownloadManagerContext";
import {
  fetchProperty,
  propertyFolderLabel,
  fetchAssetsForProperty,
  fetchAssetPhotos,
  fetchPropertyFiles,
  fetchBytes,
  runWithConcurrency,
  humanBytes,
  getAppBaseUrl,
} from "./shared";
import { sanitize } from "@/features/exports/services/_helpers";
import { fetchResolvedSurveyAttributes } from "@/features/properties/hooks/usePropertyAttributes";

interface Args {
  projectId: string;
  propertyId: string;
}

const CONCURRENCY = 6;
const MAX_EMBED_BYTES = 250 * 1024 * 1024; // 250 MB — generous, browser memory allows it

export async function buildSiteBundle(
  { projectId, propertyId }: Args,
  ctx: BuildContext,
): Promise<Blob> {
  const property = await fetchProperty(propertyId);
  if (!property) throw new Error("Property not found");
  const propertyLabel = propertyFolderLabel(property);
  const startedAt = new Date().toISOString();

  const zip = new JSZip();
  const root = zip.folder(propertyLabel)!;
  const skippedLarge: string[] = [];

  // INFO.txt
  root.file(
    "INFO.txt",
    [
      `Site Bundle — generated ${startedAt}`,
      ``,
      `Store Number:     ${property.store_number ?? "—"}`,
      `Name:             ${property.name ?? "—"}`,
      `Address:          ${property.address ?? "—"}`,
      `City/State/ZIP:   ${[property.city, property.state, property.zip_code].filter(Boolean).join(", ") || "—"}`,
      `Square Footage:   ${property.square_footage ?? "—"}`,
      ``,
      `Contents:`,
      `  digital_twin/   point clouds, drawings, 3D models, IFC, BIM`,
      `  photos/         property and asset photos by category`,
      `  data/           survey XLSX, asset register XLSX`,
      `  VR_TOURS.txt    virtual tour links (if available)`,
      `  MANIFEST.txt    files larger than ${humanBytes(MAX_EMBED_BYTES)} listed there with URLs`,
    ].join("\n"),
  );

  ctx.setProgress(5, "Listing files…");

  // ── Digital twin files ───────────────────────────────────────────────────
  const files = await fetchPropertyFiles(propertyId);
  const twinFolder = root.folder("digital_twin")!;
  const twinProgress = { done: 0 };
  const twinTotal = files.length;

  const twinTasks: Array<() => Promise<void>> = files.map((f) => async () => {
    if (ctx.signal.aborted) return;
    const category = sanitize(f.file_category || "other");
    const filename = sanitize(f.filename || f.id);
    const url = f.download_url || (typeof f.file_path === "string" && f.file_path.startsWith("http") ? f.file_path : null);
    const tooBig = typeof f.file_size === "number" && f.file_size > MAX_EMBED_BYTES;
    if (!url || tooBig) {
      skippedLarge.push(url
        ? `${category}/${filename}  (${humanBytes(f.file_size ?? 0)})  ${url}`
        : `${category}/${filename}  (no download URL)`);
    } else {
      const buf = await fetchBytes(url, { signal: ctx.signal });
      if (buf) {
        twinFolder.folder(category)!.file(filename, buf);
      } else {
        skippedLarge.push(`${category}/${filename}  (download failed)  ${url}`);
      }
    }
    twinProgress.done++;
    if (twinTotal > 0) {
      ctx.setProgress(5 + Math.floor((twinProgress.done / twinTotal) * 25), `Twin files ${twinProgress.done} / ${twinTotal}`);
    }
  });

  await runWithConcurrency(twinTasks, CONCURRENCY, ctx.signal);

  // ── Photos ───────────────────────────────────────────────────────────────
  ctx.setProgress(32, "Listing photos…");
  const assets = await fetchAssetsForProperty(propertyId);
  const assetMap = new Map(assets.map((a) => [a.id, a]));
  const assetPhotos = await fetchAssetPhotos(assets.map((a) => a.id));
  const photosFolder = root.folder("photos")!;
  const totalPhotos = assetPhotos.length;
  const photoProgress = { done: 0 };

  const photoTasks: Array<() => Promise<void>> = [];

  for (const photo of assetPhotos) {
    photoTasks.push(async () => {
      if (ctx.signal.aborted) return;
      if (photo.file_path) {
        const asset = assetMap.get(photo.asset_id);
        const assetLabel = sanitize(asset?.name || photo.asset_id);
        const cat = sanitize(photo.category || "general");
        const buf = await fetchBytes(photo.file_path, { bucket: "asset-documents", signal: ctx.signal });
        if (buf) {
          const name = sanitize(photo.document_name || photo.id) + ".jpg";
          photosFolder.folder("assets")!.folder(assetLabel)!.folder(cat)!.file(name, buf);
        }
      }
      photoProgress.done++;
      if (totalPhotos > 0) {
        ctx.setProgress(32 + Math.floor((photoProgress.done / totalPhotos) * 50), `Photos ${photoProgress.done} / ${totalPhotos}`);
      }
    });
  }

  await runWithConcurrency(photoTasks, CONCURRENCY, ctx.signal);
  if (ctx.signal.aborted) throw new Error("Cancelled");

  // ── data/survey.xlsx ─────────────────────────────────────────────────────
  ctx.setProgress(84, "Building survey data…");
  root.folder("data")!.file("survey.xlsx", await buildSurveyXlsx(propertyId));

  // ── data/assets.xlsx ─────────────────────────────────────────────────────
  ctx.setProgress(88, "Building asset data…");
  root.folder("data")!.file("assets.xlsx", buildAssetsXlsx(assets as unknown as AssetRow[], propertyLabel, projectId));

  // ── MANIFEST.txt ─────────────────────────────────────────────────────────
  if (skippedLarge.length > 0) {
    root.file(
      "MANIFEST.txt",
      [
        `Large files not embedded (over ${humanBytes(MAX_EMBED_BYTES)} or unavailable).`,
        `Download them directly from the property files page.`,
        ``,
        ...skippedLarge,
      ].join("\n"),
    );
  }

  ctx.setProgress(92, "Compressing…");
  const blob = await zip.generateAsync({ type: "blob", compression: "DEFLATE", compressionOptions: { level: 5 } });
  ctx.setProgress(100, "Done");
  return blob;
}

async function buildSurveyXlsx(propertyId: string): Promise<ArrayBuffer> {
  // Resolved through the prod_data gate (prod_data when fully answered, else data).
  const fields = (await fetchResolvedSurveyAttributes(propertyId)) as unknown as Array<Record<string, unknown>>;

  const rows: Array<Array<string>> = [["Category", "Question", "Answer"]];
  if (fields.length > 0) {
    function walk(field: Record<string, unknown>, parent: string) {
      const children = field.fields as Array<Record<string, unknown>> | undefined;
      if (Array.isArray(children) && children.length > 0) {
        const newParent = parent ? `${parent} > ${field.title}` : String(field.title);
        for (const c of children) walk(c, newParent);
        return;
      }
      const cleanTitle = String(field.title ?? "").replace(/\s*\[.*?\]\s*$/, "");
      let answer = "";
      const t = field.type;
      const v = field.value;
      if (t === "photos") {
        answer = Array.isArray(v) && v.length > 0 ? `${v.length} photo(s) attached` : "";
      } else if (t === "area") {
        answer = typeof v === "string" ? v.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim() : "";
      } else if (t === "multiSelect") {
        answer = Array.isArray(v) && v.length > 0 ? v.join("; ") : "";
      } else {
        answer = v == null ? "" : String(v);
      }
      const category = parent.split(" > ")[0] || "General";
      rows.push([category, cleanTitle, answer]);
    }
    for (const f of fields) walk(f, "");
  }

  return buildStyledXlsx(rows, "Survey");
}

interface AssetRow {
  id: string;
  name: string | null;
  description: string | null;
  category_id: string | null;
  asset_categories: { name: string } | { name: string }[] | null;
  manufacturer: string | null;
  model_name: string | null;
  model_number: string | null;
  serial_number: string | null;
  manufacture_date: string | null;
  estimated_age_years: number | null;
  condition_status: string | null;
  category_attributes: Record<string, unknown> | null;
  notes: string | null;
  zone_id: string | null;
  project_zones: { name: string } | { name: string }[] | null;
  nameplate_photo_url: string | null;
  asset_photo_url: string | null;
}

function relName(rel: { name: string } | { name: string }[] | null): string | null {
  const r = Array.isArray(rel) ? rel[0] : rel;
  return r?.name ?? null;
}

function buildAssetsXlsx(assets: AssetRow[], propertyLabel: string, _projectId: string): ArrayBuffer {
  const appBase = getAppBaseUrl();
  const header = [
    "Property", "Asset Name", "Category", "Description", "Manufacturer", "Model Name", "Model Number",
    "Serial Number", "Manufacture Date", "Estimated Age (Years)", "Condition Status",
    "Location in Store", "Notes", "Asset URL", "Nameplate Photo", "Asset Photo",
  ];
  const rows: Array<Array<string | number | null>> = [header];
  for (const a of assets) {
    rows.push([
      propertyLabel,
      a.name,
      relName(a.asset_categories),
      a.description,
      a.manufacturer,
      a.model_name,
      a.model_number,
      a.serial_number,
      a.manufacture_date,
      a.estimated_age_years,
      a.condition_status,
      relName(a.project_zones),
      a.notes,
      `${appBase}/asset/${a.id}`,
      a.nameplate_photo_url,
      a.asset_photo_url,
    ]);
  }
  return buildStyledXlsx(rows, "Assets");
}

const HEADER_STYLE = {
  font: { bold: true, color: { rgb: "FFFFFF" } },
  fill: { fgColor: { rgb: "4472C4" } },
  alignment: { horizontal: "center" as const },
};

function buildStyledXlsx(rows: Array<Array<string | number | null>>, sheetName: string): ArrayBuffer {
  const ws = XLSX.utils.aoa_to_sheet(rows);
  const range = XLSX.utils.decode_range(ws["!ref"] ?? "A1");
  for (let c = range.s.c; c <= range.e.c; c++) {
    const ref = XLSX.utils.encode_cell({ r: 0, c });
    if (ws[ref]) {
      ws[ref].s = HEADER_STYLE;
    }
  }
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  return XLSX.write(wb, { type: "array", bookType: "xlsx" }) as ArrayBuffer;
}
